<template>
  <action :action="action" showText v-on="$listeners" v-resize="onResize">
    <template v-slot:default="{ show }">
      <v-expand-transition>
        <div v-if="show && !event" class="d-flex" :class="noHistory ? 'justify-center' : 'justify-end'">
          <v-btn v-if="!isAcademySubject" text @click.stop="popupEditorDialog = true" :class="{ 'primary-color': noHistory }">
            {{ noHistory ? "Continue Submission" : "Edit" }}
          </v-btn>
          <v-dialog
            max-width="800"
            :fullscreen="smallScreen"
            scrollable
            v-model="popupEditorDialog"
            persistent
            @keydown.esc="popupEditorDialog = false"
          >
            <v-card :loading="loading">
              <v-toolbar flat dense>
                <v-toolbar-title v-text=" isAcademySubject ? 'Discussion Subject' : action.label || 'Response'" />
                <v-spacer></v-spacer>
                <v-toolbar-items>
                  <v-btn icon text id="dismiss" @click="closeDialog()">
                    <v-icon>$close</v-icon>
                  </v-btn>
                </v-toolbar-items>
              </v-toolbar>
              <v-card-text
                style="border-top: 1px solid var(--v-primary-base); border-bottom: 1px solid var(--v-primary-base)"
              >
              <ValidationProvider ref="subjectValidator" :name="action.label || 'Response'" rules="required|customTextValidation" v-slot="{ errors, valid }">
                <v-textarea
                  solo
                  flat
                  auto-grow
                  no-resize
                  v-model="text"
                  class="ma-2"
                  :error-messages="errors"
                  dense
                  :placeholder="placeHolderComp"
                  @keyup="disableSubmitBtn = valid"
                  @focus="disableSubmitBtn = valid"
                  @keydown.enter.shift.exact.prevent="enter()"
                  id="response"
                  persistent-hint
                />
              </ValidationProvider>
              </v-card-text>
              <!-- AI Suggestion Component -->
              <v-card-actions class="ai-actions d-flex align-center justify-space-between" v-if="text.trim().length > 0">
                <div class="ai-wrapper d-flex align-center">
                  <AiSuggestion
                    :logRefId="suggestionLogId"
                    log-ref-table="query_header_input"
                    :text="text"
                    target="subject"
                    @useSuggestion="text = $event"
                    help-text="Need help crafting an engaging subject? Click the magic wand for AI suggestions!"
                   />
                </div>
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <v-btn v-on="on" small icon id="submit" :disabled="!disableSubmitBtn" class="send-btn ml-4 mb-2" @click="enter()">
                      <v-icon color="primary">$send</v-icon>
                    </v-btn>
                  </template>
                  <span v-text="$t('discussion.response.send')" />
                </v-tooltip>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </div>
      </v-expand-transition>
    </template>
  </action>
</template>

<script lang="ts">
import Vue, { PropType, VueConstructor } from "vue";
import Action from "./Action.vue";
import { VTextField } from "vuetify/lib";
import { ChatBotAction, ChatBotEvent, QueryService } from "@/service";
import AiSuggestion from "@/components/AI/AiSuggestion.vue";
import { ValidationProvider } from "vee-validate";

export default (Vue as VueConstructor<Vue & { $refs: { field: typeof VTextField } }>).extend({
  name: "LongTextActionComponent",
  components: {
    Action,
    AiSuggestion, // Register new component
    ValidationProvider
  },
  props: {
    action: {
      type: Object as PropType<ChatBotAction>,
      required: true,
    },
    isAcademySubject: {
      type: Boolean,
      default: false,
    },
    academySubjectContent: {
      type: String,
      default: "",
    },
    academyDraftId: {
      type: Number,
      required: false,
    },
  },
  data: () => ({
    event: undefined as ChatBotEvent | undefined,
    text: "",
    windowHeight: 0,
    popupEditorDialog: true,
    disableSubmitBtn: true,
    loading: false
  }),
  computed: {
    minCount(): number {
      return this.action?.minimum ?? 1;
    },
    smallScreen(): boolean {
      return this.windowHeight < 400;
    },
    placeHolderComp() {
      return this.action.hint ? this.action.hint : this.$t('discussion.response.startTyping');
    },
    noHistory(): boolean {
      return this.action.clientData?.history ? false : true;
    },
    suggestionLogId(): number | null {
      if (this.isAcademySubject && this.academyDraftId) {
        return this.academyDraftId;
      }
      return this.action.clientData?.queryDraftId ?? null;
    },
  },
  methods: {
    onResize(): void {
      // TODO This Value is treated differently by mobile safari and mobile chrome. Try to standardize on it
      this.windowHeight = window.innerHeight;
    },
    enter(): void {
      if(!this.disableSubmitBtn) return;

      if(this.isAcademySubject) {
        this.submitAcademySubject();
        return;
      }

      if (this.text.trim().length) {
        this.popupEditorDialog = false;
        this.event = {
          type: "respond",
          ts: Date.now(),
          clientData: this.action.clientData,
          responses: [this.text],
        };
        this.$emit("callback", this.event);
      }
    },
    async submitAcademySubject() {
      try {
        if (!this.text.trim().length) return;
        this.loading = true;
       const historyData = await QueryService.updateQueryHeaderInput({
          id: this.academyDraftId,
          requestBody: {
            history: this.text,
          }
        });
        
      } catch (error) {
        console.error("Error submitting academy subject:", error);
      } finally {
        this.loading = false;
        this.$emit('reload-plan-data');
        this.closeDialog();
      }
    },
    loadAcademySubject() {
      this.text = this.academySubjectContent;
    },
    closeDialog(): void {
      this.popupEditorDialog = false; 
      this.$emit('close-history-dialog')
    },
  },
  created() {
    if (this.action.responses?.length) {
      this.text = this.action.responses[0];
    }
  },
  mounted() {
    if (this.isAcademySubject && this.academyDraftId) {
        this.loadAcademySubject();
    }
  }
});
</script>
<style lang="scss" scoped>
.primary-color {
  color: var(--v-primary-base) !important;
}
.ai-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  flex-wrap: nowrap;
}
.ai-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-grow: 1;
}
.send-btn {
  min-width: unset !important;
  padding: 0 !important;
  margin-top: 10 !important;
  align-self: flex-start; /* Ensures it stays at the top */
}
</style>